import React, { useState, useEffect } from 'react';
import { connect, ConnectedProps } from 'react-redux';
import {
  Typography,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import {
  setSearchInput,
  setSortBy,
  setRecommendationFilter,
  setEntityTypeFilter,
  setActionInitiated,
  createProviderSearch,
  fetchProviderData,
} from '@apps/shared/src/providerSearchAdmin/actions';
import Sidebar from '../shared/providerSearchSidebar';
import CreateProviderSearch from './CreateProviderSearch';
import { roles as mediviRoles } from '../shared/globals';
import { RootState } from '../store';

const useStyles = makeStyles(theme => ({
  container: {
    padding: '0 1rem 4rem 1rem',
    width: 310,
    '& > div': {
      marginTop: '.5rem',
    },
  },
  sortContainer: {
    display: 'flex',
    alignItems: 'center',
  },
  select: {
    '& .MuiSelect-selectMenu': {
      background: '#ffffff',
      border: '1px solid #E5EBF5',
      padding: '12px 16px',
    },
  },
  textField: {
    '& .MuiInputBase-root': {
      background: '#ffffff',
      border: '1px solid #E5EBF5',
      borderRadius: 4,
    },
    '& .MuiOutlinedInput-input': {
      padding: '10px 14px',
    },
    '& .MuiInputLabel-root': {
      background: '#ffffff',
      padding: '0 1px',
    },
  },
  sortHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  header: theme.sidebar.headerText,
  menuItem: {
    ...theme.selectMenuItem,
    padding: '12px 16px',
    minHeight: '48px',
  },
  clearButton: {
    marginTop: '1rem',
    fontSize: '.8rem',
    fontWeight: 500,
    color: '#ff0000',
    cursor: 'pointer',
    textDecoration: 'underline',
    background: 'none',
    border: 'none',
    padding: 0,
    '&:hover': {
      textDecoration: 'none',
    },
  },
}));

const mapStateToProps = (state: RootState) => ({
  pageNo: state.providerSearchAdmin.pageNo,
  pageSize: state.providerSearchAdmin.pageSize,
  searchInput: state.providerSearchAdmin.searchInput,
  sortBy: state.providerSearchAdmin.sortBy,
  recommendation: state.providerSearchAdmin.recommendation,
  entityType: state.providerSearchAdmin.entityType,
  actionInitiated: state.providerSearchAdmin.actionInitiated,
  hasError: state.providerSearchAdmin.hasError,
  isActionPending: state.providerSearchAdmin.isActionPending,
  roles: state.user.currentUser?.roles || [],
});

const mapDispatchToProps = {
  setSearchInput,
  setSortBy,
  setRecommendationFilter,
  setEntityTypeFilter,
  setActionInitiated,
  fetchProviderData,
  createProviderSearch,
};

const connector = connect(mapStateToProps, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

type Props = PropsFromRedux;

export function ProviderSearchUpdatePane({
  setSearchInput,
  setSortBy,
  setRecommendationFilter,
  setEntityTypeFilter,
  setActionInitiated,
  fetchProviderData,
  createProviderSearch,
  pageNo,
  pageSize,
  searchInput,
  sortBy,
  recommendation,
  entityType,
  actionInitiated,
  roles,
  hasError,
  isActionPending,
}: Props): JSX.Element {
  const classes = useStyles();
  const [localSearchInput, setLocalSearchInput] = useState('');
  const [insertPopupOpen, setInsertPopupOpen] = useState(false);

  const hasUpdateProviderExperience = roles.indexOf(mediviRoles.updateProviderExperience) > -1;

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setSearchInput(localSearchInput);
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [localSearchInput, setSearchInput]);

  useEffect(() => {
    setActionInitiated('');
  }, [setActionInitiated]);

  useEffect(() => {
    fetchProviderData(1, 25, searchInput, sortBy, recommendation, entityType);
  }, [searchInput, sortBy, recommendation, entityType]);

  // Function to clear all filters
  const handleClearFilters = () => {
    setSearchInput('');
    setSortBy('');
    setRecommendationFilter('');
    setEntityTypeFilter('');
    setLocalSearchInput(''); // Clear local state as well
  };

  const handleOpenInsertPopup = () => {
    setInsertPopupOpen(true);
  };

  const handleCloseInsertPopup = () => {
    setActionInitiated('');
    setInsertPopupOpen(false);
  };

  const handleInsertData = (formData: any) => {
    createProviderSearch(formData);
    fetchProviderData(1, 25, searchInput, sortBy, recommendation, entityType);
  };

  const handleActionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    setActionInitiated(value);
    if (value === 'insert') {
      handleOpenInsertPopup();
    }
  };

  const handleCancel = () => {
    setActionInitiated('');
  };

  return (
    <Sidebar hideButton className={classes.container} toggleButtonTitle="Provider Search update">
      {hasUpdateProviderExperience && (
        <>
          <div className={classes.sortHeader}>
            <Typography style={{ marginTop: '1rem', fontSize: '1rem', fontWeight: 600 }}>
              Insert/Update:
            </Typography>
            {actionInitiated === 'update' && (
              <button onClick={handleCancel} className={classes.clearButton} type="button">
                Cancel
              </button>
            )}
          </div>
          <RadioGroup value={actionInitiated} onChange={handleActionChange} row>
            <FormControlLabel
              value="insert"
              control={<Radio color="primary" />}
              label="Insert new record"
            />
            <FormControlLabel
              value="update"
              control={<Radio color="primary" />}
              label="Update existing record"
            />
          </RadioGroup>
        </>
      )}

      <div className={classes.sortHeader}>
        <Typography style={{ marginTop: '1rem', fontSize: '1rem', fontWeight: 600 }}>
          Sort and Filter:
        </Typography>
        <button onClick={handleClearFilters} className={classes.clearButton} type="button">
          Clear
        </button>
      </div>

      <FormControl fullWidth className={classes.textField}>
        <Typography className={classes.header}>Keyword Search</Typography>
        <TextField
          id="keyword-search"
          value={localSearchInput}
          onChange={e => setLocalSearchInput(e.target.value as string)}
          fullWidth
          variant="outlined"
          InputProps={{
            style: { backgroundColor: '#ffffff', borderRadius: 4, border: '1px solid #E5EBF5' },
          }}
        />
      </FormControl>

      <FormControl fullWidth>
        <Typography className={classes.header}>Sort By</Typography>
        <Select
          value={sortBy}
          onChange={e => setSortBy(e.target.value as string)}
          className={classes.select}
          disableUnderline
        >
          <MenuItem className={classes.menuItem} value="providerName">
            Provider Name
          </MenuItem>
          <MenuItem className={classes.menuItem} value="npi">
            NPI
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <Typography className={classes.header}>Filter by Recommendation</Typography>
        <Select
          value={recommendation}
          onChange={e => setRecommendationFilter(e.target.value as string)}
          className={classes.select}
          disableUnderline
        >
          <MenuItem className={classes.menuItem} value="CAUTION">
            Caution
          </MenuItem>
          <MenuItem className={classes.menuItem} value="UNFRIENDLY">
            Unfriendly
          </MenuItem>
          <MenuItem className={classes.menuItem} value="DELISTED">
            Delisted
          </MenuItem>
        </Select>
      </FormControl>

      <FormControl fullWidth>
        <Typography className={classes.header}>Filter by Entity Type</Typography>
        <Select
          value={entityType}
          onChange={e => setEntityTypeFilter(e.target.value as string)}
          className={classes.select}
          disableUnderline
        >
          <MenuItem className={classes.menuItem} value="2">
            Facility
          </MenuItem>
          <MenuItem className={classes.menuItem} value="1">
            Professional
          </MenuItem>
        </Select>
      </FormControl>

      <CreateProviderSearch
        open={insertPopupOpen}
        onClose={handleCloseInsertPopup}
        onInsert={handleInsertData}
        hasError={hasError}
        isActionPending={isActionPending}
        actionInitiated={actionInitiated}
      />
    </Sidebar>
  );
}

export default connector(ProviderSearchUpdatePane);
